import React, { useState } from "react";
import { Loader } from "lucide-react";
import Sidebar from "../../components/Sidebar";
import PauseAllCampaigns from "../CouponCampaigns/components/PauseAllCampaigns";
import {
  useRatePushPreview,
  useRatePushSettings,
  useRatePushTest,
  useUpdateRatePushSettings,
} from "./hooks/useRatePush";

const KINDS = [
  { kind: "send", title: "Send and convert rates", when: "09:00 Lagos time, every day" },
  { kind: "gift", title: "Gift card sell rates", when: "15:00 Lagos time, every day" },
];

const PushPreview = ({ result }) => (
  <div className="mt-3 space-y-2">
    {result.groups.length === 0 && <p className="text-sm text-gray-500">Nothing to send: no devices, or no rates came back.</p>}
    {result.groups.map((g, i) => (
      <div key={i} className="p-3 text-sm bg-gray-50 rounded-lg border border-gray-200">
        <p className="font-semibold">{g.title}</p>
        <p className="text-gray-700 whitespace-pre-line">{g.body}</p>
        <p className="mt-1 text-xs text-gray-500">{g.devices} device(s)</p>
      </div>
    ))}
  </div>
);

const ConsumerNotificationSettings = () => {
  const { data, isLoading, isError } = useRatePushSettings();
  const update = useUpdateRatePushSettings();
  const preview = useRatePushPreview();
  const test = useRatePushTest();
  const [email, setEmail] = useState("");
  const [previews, setPreviews] = useState({});

  const enabled = data?.enabled === true;

  const showPreview = (kind) =>
    preview.mutate(kind, { onSuccess: (res) => setPreviews((p) => ({ ...p, [kind]: res })) });

  return (
    <div className="flex overflow-hidden flex-col h-screen md:flex-row">
      <Sidebar />

      <div className="overflow-y-auto flex-1 p-4 bg-white md:py-7">
        <h1 className="py-4 mb-0 text-xl font-bold md:text-2xl md:mb-6">Notification Settings</h1>

        <div className="max-w-2xl">
          <PauseAllCampaigns />
        </div>

        <section className="p-4 mb-6 max-w-2xl rounded-xl border border-gray-200">
          <div className="flex gap-4 justify-between items-center">
            <div>
              <h2 className="text-base font-semibold">Daily rate pushes</h2>
              <p className="text-sm text-gray-600">
                Two pushes a day to every user with the app who has not opted out of marketing. Rates are the market
                rate with no Novacrust spread.
              </p>
            </div>
            {isLoading ? (
              <Loader className="w-5 h-5 animate-spin" />
            ) : (
              <button
                type="button"
                role="switch"
                aria-checked={enabled}
                aria-label="Daily rate pushes"
                disabled={isError || update.isPending}
                onClick={() => update.mutate(!enabled)}
                className={`relative h-6 w-11 shrink-0 rounded-full transition-colors disabled:opacity-50 ${enabled ? "bg-[#013941]" : "bg-gray-300"}`}
              >
                <span
                  className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${enabled ? "left-[22px]" : "left-0.5"}`}
                />
              </button>
            )}
          </div>
          {isError && <p className="mt-2 text-sm text-red-600">Could not load the setting.</p>}
          <p className="mt-3 text-sm font-medium">{enabled ? "On" : "Off"}</p>
        </section>

        {KINDS.map(({ kind, title, when }) => (
          <section key={kind} className="p-4 mb-6 max-w-2xl rounded-xl border border-gray-200">
            <h2 className="text-base font-semibold">{title}</h2>
            <p className="text-sm text-gray-600">{when}</p>

            <div className="flex flex-wrap gap-2 mt-3">
              <button
                type="button"
                onClick={() => showPreview(kind)}
                disabled={preview.isPending}
                className="px-4 py-2 text-sm font-medium rounded-3xl border border-[#013941] text-[#013941] disabled:opacity-50"
              >
                Preview
              </button>
              <button
                type="button"
                onClick={() => test.mutate({ kind, email: email.trim() })}
                disabled={test.isPending || !email.trim()}
                className="px-4 py-2 text-sm font-medium text-white rounded-3xl bg-[#013941] disabled:opacity-50"
              >
                Send a test
              </button>
            </div>
            {previews[kind] && <PushPreview result={previews[kind]} />}
          </section>
        ))}

        <div className="max-w-2xl">
          <label htmlFor="test-email" className="block mb-1 text-sm font-medium">
            Test recipient
          </label>
          <input
            id="test-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@example.com"
            className="px-3 py-2 w-full text-sm rounded-lg border border-gray-300"
          />
          <p className="mt-1 text-xs text-gray-500">
            A test goes only to this user&apos;s devices and works whether the pushes are on or off.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ConsumerNotificationSettings;
